
import React from 'react';
import { useOrganizationClubs } from '@/hooks/useOrganizationClubs';
import { useSeasons } from '@/hooks/useSeasons';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, AlertCircle } from 'lucide-react';
import { Team, ClubCoach } from '@/types/team';
import { cn } from '@/lib/utils';

interface ValidationErrors {
  teamName?: string;
  selectedClubId?: string;
  selectedSeasonId?: string;
}

interface TeamDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingTeam: Team | null;
  teamName: string;
  setTeamName: (name: string) => void;
  selectedClubId: string;
  setSelectedClubId: (id: string) => void;
  selectedSeasonId: string;
  setSelectedSeasonId: (id: string) => void;
  selectedCoachId: string;
  setSelectedCoachId: (id: string) => void;
  selectedDivisionId: string;
  setSelectedDivisionId: (id: string) => void;
  clubCoaches: ClubCoach[];
  isSubmitting: boolean;
  validationErrors: ValidationErrors;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  organizationId?: string;
}

export const TeamDialog = ({
  isOpen,
  onOpenChange,
  editingTeam,
  teamName,
  setTeamName,
  selectedClubId,
  setSelectedClubId,
  selectedSeasonId,
  setSelectedSeasonId,
  selectedCoachId,
  setSelectedCoachId,
  selectedDivisionId,
  setSelectedDivisionId,
  clubCoaches,
  isSubmitting,
  validationErrors,
  onSubmit,
  onClose,
  organizationId
}: TeamDialogProps) => {
  const { clubs } = useOrganizationClubs(organizationId);
  const { seasons } = useSeasons(selectedClubId);

  // Fetch divisions for the selected season
  const { data: divisions } = useQuery({
    queryKey: ['divisions', selectedSeasonId],
    queryFn: async () => {
      if (!selectedSeasonId) return [];
      
      const { data, error } = await supabase
        .from('divisions')
        .select('*')
        .eq('season_id', selectedSeasonId)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedSeasonId,
  });

  // Reset division when season changes
  React.useEffect(() => {
    if (!editingTeam) {
      setSelectedDivisionId('no-division');
    }
  }, [selectedSeasonId, editingTeam, setSelectedDivisionId]);

  // Check if the currently selected coach is available in the club
  const isCurrentCoachAvailable = selectedCoachId === 'no-coach' || selectedCoachId === '' || 
    clubCoaches.some(coach => coach.id === selectedCoachId);

  const getCoachSelectValue = () => {
    if (!selectedCoachId || selectedCoachId === 'none') {
      return 'no-coach';
    }
    return isCurrentCoachAvailable ? selectedCoachId : 'no-coach';
  };

  const getDivisionSelectValue = () => {
    if (!selectedDivisionId || selectedDivisionId === 'none') {
      return 'no-division';
    }
    return selectedDivisionId;
  };

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const canSubmit = teamName.trim() && selectedClubId && selectedSeasonId;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Team
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingTeam ? 'Edit Team' : 'Create New Team'}
          </DialogTitle>
          <DialogDescription>
            {editingTeam ? 'Update the team details.' : 'Enter the details for the new team. Fields marked with * are required.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="club-select" className="flex items-center gap-1">
                Club <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={selectedClubId} 
                onValueChange={setSelectedClubId} 
                required
              >
                <SelectTrigger className={cn(validationErrors.selectedClubId && "border-red-500")}>
                  <SelectValue placeholder="Select a club" />
                </SelectTrigger>
                <SelectContent>
                  {clubs.map((club) => (
                    <SelectItem key={club.id} value={club.id}>
                      {club.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.selectedClubId && (
                <div className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.selectedClubId}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="season-select" className="flex items-center gap-1">
                Season <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={selectedSeasonId} 
                onValueChange={setSelectedSeasonId} 
                required
              >
                <SelectTrigger className={cn(validationErrors.selectedSeasonId && "border-red-500")}>
                  <SelectValue placeholder="Select a season" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map((season) => (
                    <SelectItem key={season.id} value={season.id}>
                      {season.season_year} - {season.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.selectedSeasonId && (
                <div className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.selectedSeasonId}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="division-select">Division (Optional)</Label>
              <Select value={getDivisionSelectValue()} onValueChange={setSelectedDivisionId}>
                <SelectTrigger>
                  <SelectValue placeholder={selectedSeasonId ? "Select a division" : "Select a season first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-division">No division assigned</SelectItem>
                  {divisions?.map((division) => (
                    <SelectItem key={division.id} value={division.id}>
                      {division.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSeasonId && (!divisions || divisions.length === 0) && (
                <p className="text-sm text-muted-foreground">
                  No divisions created for this season yet. Create divisions first to assign teams.
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="team-name" className="flex items-center gap-1">
                Team Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="team-name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Enter team name"
                className={cn(validationErrors.teamName && "border-red-500")}
                required
              />
              {validationErrors.teamName && (
                <div className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {validationErrors.teamName}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="coach-select">Coach (Optional)</Label>
              <Select value={getCoachSelectValue()} onValueChange={setSelectedCoachId}>
                <SelectTrigger>
                  <SelectValue placeholder={selectedClubId ? "Select a coach" : "Select a club first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-coach">No coach assigned</SelectItem>
                  {clubCoaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id}>
                      {coach.first_name} {coach.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedClubId && clubCoaches.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No coaches assigned to this club yet. Assign coaches to the club first.
                </p>
              )}
              {!isCurrentCoachAvailable && selectedCoachId !== 'no-coach' && selectedCoachId !== '' && (
                <p className="text-sm text-amber-600">
                  The previously assigned coach is no longer available in this club.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || (!canSubmit && !editingTeam)}
              className={cn(!canSubmit && !editingTeam && "opacity-50")}
            >
              {isSubmitting ? 'Saving...' : editingTeam ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
          {hasValidationErrors && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2 text-sm text-red-800">
                <AlertCircle className="h-4 w-4" />
                Please fix the validation errors above before submitting.
              </div>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
